/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Link,
  Divider
} from '@mui/material';
import {
  Shield as ShieldIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugIcon,
  Feedback as FeedbackIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const BetaTesterBanner: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert 
        severity="info" 
        sx={{ 
          backgroundColor: '#1a237e', // Deep blue background
          color: 'white',
          border: '2px solid #3949ab', // Lighter blue border
          borderRadius: 2,
          '& .MuiAlert-icon': {
            color: '#ffd700' // Gold icon
          }
        }}
        icon={<ShieldIcon />}
        action={
          <IconButton
            aria-label="expand"
            size="small"
            onClick={handleToggleExpanded}
            sx={{ color: 'white' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
      >
        <AlertTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          {t('hailNobleBetaKnight')}
        </AlertTitle>
        <Typography variant="body2" sx={{ color: '#e8eaf6' }}>
          {t('betaTesterDescription')}
        </Typography>
      </Alert>

      <Collapse in={expanded}>
        <Box 
          sx={{ 
            mt: 1,
            p: 2,
            backgroundColor: '#e8eaf6', // Light blue background
            borderRadius: 1,
            border: '1px solid #3949ab'
          }}
        >
          {/* Privacy Information Section - keeping blue theme */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#1a237e', borderRadius: 1, border: '1px solid #3949ab' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon fontSize="small" />
              {t('privacyDataProtection')}
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ color: '#e8eaf6' }}>
              {t('privateDebatesWarning')}
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#e8eaf6', display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon fontSize="small" />
              {t('publicContentReminder')}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold' }}>
            {t('yourQuestAsBetaKnight')}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" paragraph sx={{ color: '#1a237e' }}>
              {t('betaTesterPowerDescription')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip 
                icon={<BugIcon />} 
                label={t('huntBugsGlitches')} 
                variant="outlined" 
                sx={{ borderColor: '#3949ab', color: '#1a237e' }}
              />
              <Chip 
                icon={<FeedbackIcon />} 
                label={t('shareYourWisdom')} 
                variant="outlined" 
                sx={{ borderColor: '#3949ab', color: '#1a237e' }}
              />
              <Chip 
                icon={<ShieldIcon />} 
                label={t('testNewFeatures')} 
                variant="outlined" 
                sx={{ borderColor: '#3949ab', color: '#1a237e' }}
              />
            </Box>
          </Box>

          <Typography variant="body2" paragraph sx={{ color: '#1a237e' }}>
            {t('rememberRealmBeingForged')}
          </Typography>
          
          <Typography variant="body2" component="ul" sx={{ color: '#1a237e', pl: 2 }}>
            <li>{t('featuresBeingRefined')}</li>
            <li>{t('uiElementsChanging')}</li>
            <li>{t('limitedAiResponses')}</li>
          </Typography>

          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#ffd700', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
              {t('reportIssuesText')}{' '}
              <Link 
                href="mailto:vincent@gauntletdebates.com" 
                sx={{ color: '#1a237e', textDecoration: 'underline' }}
              >
                vincent@gauntletdebates.com
              </Link>
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: '#3949ab', fontStyle: 'italic', mt: 1 }}>
            {t('feedbackForgePath')}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default BetaTesterBanner; 